import { motion } from 'framer-motion';
import { User, Save, CheckCircle, AlertTriangle, Camera } from 'lucide-react';
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { supabase } from '../../../../lib/supabase';
import { logEvent } from '../../../../services/loggingService';
import { getCurrentUserId } from '../../../../utils/authUtils';

import { LabelValueRow } from './LabelValueRow';
import { SettingsSection } from './SettingsSection';

interface ProfileData {
  name: string;
  email: string;
  phone: string;
  gender: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  avatar_url: string;
}

export interface ProfileInformationTabRef {
  save: () => Promise<void>;
}

interface ProfileInformationTabProps {
  onNavigateToTab?: (tab: 'privacy' | 'onboarding') => void;
  twoColumn?: boolean;
  grid3?: boolean;
  sectioned?: boolean;
  editing?: boolean;
}

const ProfileInformationTab = forwardRef<ProfileInformationTabRef, ProfileInformationTabProps>(function ProfileInformationTab(
  { twoColumn, grid3, sectioned, editing = true },
  ref
) {
  const { t } = useTranslation();
  const [profileData, setProfileData] = useState<ProfileData>({
    name: '',
    email: '',
    phone: '',
    gender: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    avatar_url: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Load user profile data in background – no blocking spinner
  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { data: sessionData } = await supabase.auth.getSession();
      const authUser = sessionData?.session?.user;

      // Use maybeSingle() instead of single() to handle new users without profile records
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // Ignore "no rows" error - it's expected for new users
      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // If user record exists, populate form with existing data
      if (data) {
        setProfileData({
          name: data.name || '',
          email: data.email || (authUser?.email ?? '') || '',
          phone: data.phone || '',
          gender: data.gender || '',
          street_address: data.street_address || '',
          city: data.city || '',
          state: data.state || '',
          zip_code: data.zip_code || '',
          country: data.country || '',
          avatar_url: data.avatar_url || ''
        });
        setAvatarPreview(data.avatar_url);
      } else {
        // New user - populate with auth data
        setProfileData({
          name: '',
          email: (authUser?.email as string) || '',
          phone: (authUser?.phone as string) || '',
          gender: '',
          street_address: '',
          city: '',
          state: '',
          zip_code: '',
          country: '',
          avatar_url: ''
        });
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(t('dashboard.userProfile.profileInfo.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  // Handle avatar upload
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError(t('dashboard.userProfile.profileInfo.selectImage'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('dashboard.userProfile.profileInfo.imageSizeMax'));
      return;
    }

    setUploadingAvatar(true);
    setError(null);

    try {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      // Create file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-avatar-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: publicUrl })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      setProfileData(prev => ({ ...prev, avatar_url: publicUrl }));
      setAvatarPreview(publicUrl);
      setSuccess(t('dashboard.userProfile.profileInfo.avatarUpdated'));

      await logEvent({
        level: 'info',
        action: 'avatar_uploaded',
        details: { fileName, filePath }
      });

    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError(t('dashboard.userProfile.profileInfo.failedUploadAvatar'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Save profile data
  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('users')
        .update({
          name: profileData.name,
          phone: profileData.phone,
          gender: profileData.gender || null,
          street_address: profileData.street_address,
          city: profileData.city,
          state: profileData.state,
          zip_code: profileData.zip_code,
          country: profileData.country,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;

      setSuccess(t('dashboard.userProfile.profileInfo.profileUpdated'));

      await logEvent({
        level: 'info',
        action: 'profile_updated',
        details: { hasPhone: !!profileData.phone, hasAddress: !!profileData.street_address }
      });

    } catch (err) {
      console.error('Error saving profile:', err);
      setError(t('dashboard.userProfile.profileInfo.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSaveProfile }), [handleSaveProfile]);

  const fieldClass = 'w-full min-h-[44px] px-3 py-2 text-sm vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] focus:ring-offset-0 disabled:opacity-75 disabled:cursor-not-allowed touch-manipulation';
  const selectClass = 'w-full min-w-[10rem] min-h-[44px] px-3 py-2 text-sm rounded-md border border-[var(--vscode-input-border)] bg-[var(--vscode-input-background)] text-[var(--vscode-input-foreground)] vscode-input vscode-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] focus:ring-offset-0 disabled:opacity-75 disabled:cursor-not-allowed appearance-auto cursor-pointer touch-manipulation';
  const labelClass = 'block text-sm font-medium vscode-text-secondary mb-1';

  // Sectioned settings layout – fields always editable; save per section
  if (sectioned) {
    const input = (field: keyof ProfileData, placeholder: string, opts?: { readOnly?: boolean; type?: string }) => (
      <input
        type={opts?.type ?? 'text'}
        value={profileData[field]}
        onChange={opts?.readOnly ? undefined : (e) => handleInputChange(field, e.target.value)}
        readOnly={opts?.readOnly}
        disabled={opts?.readOnly}
        className={fieldClass}
        placeholder={placeholder}
      />
    );
    return (
      <>
        {success && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] px-3 py-2 text-sm text-[var(--vscode-success)]">
            <CheckCircle className="w-4 h-4 shrink-0" /> {success}
          </div>
        )}
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2 text-sm text-amber-700/80 dark:text-amber-400/70">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500/70 dark:text-amber-400/50" /> {error}
          </div>
        )}
        <div className="mt-6">
        <SettingsSection title={t('dashboard.userProfile.profileInfo.profile')} hideRule>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-6 gap-y-0 min-w-0">
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.photo')}>
              <div className="flex justify-end">
                <input type="file" id="avatar-upload-sectioned" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploadingAvatar} aria-label={t('dashboard.userProfile.profileInfo.changePhoto')} />
                <label htmlFor="avatar-upload-sectioned" className="relative block w-24 h-24 rounded-full overflow-hidden vscode-bg-tertiary border-2 border-dashed vscode-border flex items-center justify-center shrink-0 cursor-pointer hover:opacity-90 transition-opacity touch-manipulation">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Your profile photo" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-center px-2">
                      <span className="block text-xs font-medium vscode-text-secondary leading-tight">Upload photo</span>
                      <span className="block text-[10px] vscode-text-tertiary mt-0.5">JPG, PNG · 5 MB</span>
                    </span>
                  )}
                  {uploadingAvatar && (
                    <span className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                      <LoadingSpinner size="sm" showText={false} />
                    </span>
                  )}
                </label>
              </div>
            </LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.name')}>{input('name', t('dashboard.userProfile.profileInfo.namePlaceholder'))}</LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.email')} readOnly>{input('email', t('dashboard.userProfile.profileInfo.email'), { readOnly: true })}</LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.phone')}>{input('phone', t('dashboard.userProfile.profileInfo.phonePlaceholder'))}</LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.gender')}>
              <select
                value={profileData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className={selectClass}
                aria-label={t('dashboard.userProfile.profileInfo.gender')}
              >
                <option value="">{t('dashboard.userProfile.profileInfo.genderPreferNot')}</option>
                <option value="female">{t('dashboard.userProfile.profileInfo.female')}</option>
                <option value="male">{t('dashboard.userProfile.profileInfo.male')}</option>
              </select>
            </LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.streetAddress')}>{input('street_address', t('dashboard.userProfile.profileInfo.streetAddress'))}</LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.city')}>{input('city', t('dashboard.userProfile.profileInfo.city'))}</LabelValueRow>
            <LabelValueRow label={t('dashboard.userProfile.profileInfo.country')}>{input('country', t('dashboard.userProfile.profileInfo.country'))}</LabelValueRow>
          </dl>
          <div className="mt-3 pt-2 flex justify-end">
            <button
              type="button"
              onClick={() => handleSaveProfile()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
              style={{ background: 'var(--brand-cta-bg)' }}
            >
              {saving ? <><LoadingSpinner size="xs" showText={false} /> {t('dashboard.userProfile.profileInfo.saving')}</> : <><Save className="w-4 h-4" /> {t('dashboard.userProfile.profileInfo.saveProfile')}</>}
            </button>
          </div>
        </SettingsSection>
        </div>
      </>
    );
  }

  // 3x3 grid layout for form fields
  if (grid3) {
    return (
      <div className="vscode-bg-secondary rounded-lg border vscode-border p-4 md:col-span-3">
        <h3 className="text-sm font-semibold vscode-text-primary mb-3">Profile</h3>
        {success && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] px-3 py-2 text-sm text-[var(--vscode-success)]">
            <CheckCircle className="w-4 h-4 shrink-0" /> {success}
          </div>
        )}
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2 text-sm text-amber-700/80 dark:text-amber-400/70">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500/70 dark:text-amber-400/50" /> {error}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col items-start gap-2">
            <div className="relative">
              <div className="w-14 h-14 rounded-full overflow-hidden vscode-bg-tertiary border vscode-border flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Your profile photo" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-7 h-7 vscode-text-tertiary" />
                )}
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
            </div>
            <input type="file" id="avatar-upload-grid3" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploadingAvatar} />
            <label htmlFor="avatar-upload-grid3" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded cursor-pointer hover:opacity-90 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
              <Camera className="w-3.5 h-3.5" /> Change photo
            </label>
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.name')}</label>
            <input type="text" value={profileData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.namePlaceholder')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.email')}</label>
            <input type="email" value={profileData.email} readOnly className={`${fieldClass} vscode-text-tertiary opacity-90 cursor-not-allowed`} title={t('dashboard.userProfile.profileInfo.cannotBeChanged')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.phone')}</label>
            <input type="tel" value={profileData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.phonePlaceholder')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.gender')}</label>
            <select value={profileData.gender} onChange={(e) => handleInputChange('gender', e.target.value)} className={fieldClass}>
              <option value="">{t('dashboard.userProfile.profileInfo.genderPreferNot')}</option>
              <option value="female">{t('dashboard.userProfile.profileInfo.female')}</option>
              <option value="male">{t('dashboard.userProfile.profileInfo.male')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.streetAddress')}</label>
            <input type="text" value={profileData.street_address} onChange={(e) => handleInputChange('street_address', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.streetAddress')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.city')}</label>
            <input type="text" value={profileData.city} onChange={(e) => handleInputChange('city', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.city')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.country')}</label>
            <input type="text" value={profileData.country} onChange={(e) => handleInputChange('country', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.country')} />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button type="button" onClick={handleSaveProfile} disabled={saving} className="inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('dashboard.userProfile.profileInfo.saving') : t('dashboard.userProfile.profileInfo.saveProfile')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Kickresume-style: two cards side by side (Profile | Address)
  if (twoColumn) {
    return (
      <>
        <div className="vscode-bg-secondary rounded-lg border vscode-border p-4">
          <h3 className="text-sm font-semibold vscode-text-primary mb-3">Profile</h3>
          {success && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] px-3 py-2 text-sm text-[var(--vscode-success)]">
              <CheckCircle className="w-4 h-4 shrink-0" /> {success}
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2 text-sm text-amber-700/80 dark:text-amber-400/70">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500/70 dark:text-amber-400/50" /> {error}
            </div>
          )}
          <div className="flex gap-3 mb-3">
            <div className="relative shrink-0">
              <div className="w-14 h-14 rounded-full overflow-hidden vscode-bg-tertiary border vscode-border flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Your profile photo" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-7 h-7 vscode-text-tertiary" />
                )}
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
            </div>
            <input type="file" id="avatar-upload" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploadingAvatar} />
            <label htmlFor="avatar-upload" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded cursor-pointer hover:opacity-90 self-start text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
              <Camera className="w-3.5 h-3.5" /> Change photo
            </label>
          </div>
          <div className="space-y-2.5">
            <div>
              <label className={labelClass}>{t('dashboard.userProfile.profileInfo.name')}</label>
              <input type="text" value={profileData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.namePlaceholder')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>{t('dashboard.userProfile.profileInfo.email')}</label>
                <input type="email" value={profileData.email} readOnly className={`${fieldClass} vscode-text-tertiary opacity-90 cursor-not-allowed`} title={t('dashboard.userProfile.profileInfo.cannotBeChanged')} />
              </div>
              <div>
                <label className={labelClass}>{t('dashboard.userProfile.profileInfo.phone')}</label>
                <input type="tel" value={profileData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.phonePlaceholder')} />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('dashboard.userProfile.profileInfo.gender')}</label>
              <select value={profileData.gender} onChange={(e) => handleInputChange('gender', e.target.value)} className={fieldClass}>
                <option value="">{t('dashboard.userProfile.profileInfo.genderPreferNot')}</option>
                <option value="female">{t('dashboard.userProfile.profileInfo.female')}</option>
                <option value="male">{t('dashboard.userProfile.profileInfo.male')}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="vscode-bg-secondary rounded-lg border vscode-border p-4">
          <h3 className="text-sm font-semibold vscode-text-primary mb-3">{t('dashboard.userProfile.profileInfo.address')}</h3>
          <p className="text-xs vscode-text-tertiary mb-2">{t('dashboard.userProfile.profileInfo.optional')}</p>
          <div className="space-y-2.5">
            <div>
              <label className={labelClass}>{t('dashboard.userProfile.profileInfo.street')}</label>
              <input type="text" value={profileData.street_address} onChange={(e) => handleInputChange('street_address', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.streetAddress')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>{t('dashboard.userProfile.profileInfo.city')}</label>
                <input type="text" value={profileData.city} onChange={(e) => handleInputChange('city', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.city')} />
              </div>
              <div>
                <label className={labelClass}>{t('dashboard.userProfile.profileInfo.country')}</label>
                <input type="text" value={profileData.country} onChange={(e) => handleInputChange('country', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.country')} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={handleSaveProfile} disabled={saving} className="inline-flex items-center gap-2 min-h-[44px] px-4 text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3] touch-manipulation" style={{ background: 'var(--brand-cta-bg)' }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('dashboard.userProfile.profileInfo.saving') : t('dashboard.userProfile.profileInfo.saveProfile')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <section className="px-4 py-4" aria-label="Profile">
      <h3 className="text-sm font-semibold vscode-text-primary mb-4">{t('dashboard.userProfile.profileInfo.profile')}</h3>

      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-3 flex items-center gap-2 rounded-md bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] px-3 py-2 text-sm text-[var(--vscode-success)]"
        >
          <CheckCircle className="w-4 h-4 shrink-0" />
          {success}
        </motion.div>
      )}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-3 flex items-center gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2 text-sm text-amber-700/80 dark:text-amber-400/70"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500/70 dark:text-amber-400/50" />
          {error}
        </motion.div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        <div className="flex flex-col items-center sm:items-start gap-2 shrink-0">
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden vscode-bg-tertiary border-2 vscode-border flex items-center justify-center ring-2 ring-transparent">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Your profile photo" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 vscode-text-tertiary" />
              )}
            </div>
            {uploadingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                <LoadingSpinner size="sm" showText={false} />
              </div>
            )}
          </div>
          <input type="file" id="avatar-upload" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploadingAvatar} aria-describedby="photo-upload-hint-default" />
          <label htmlFor="avatar-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md cursor-pointer hover:opacity-90 disabled:opacity-50 transition-opacity text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
            <Camera className="w-4 h-4" /> {t('dashboard.userProfile.profileInfo.changePhoto')}
          </label>
          <p id="photo-upload-hint-default" className="text-xs vscode-text-tertiary">Upload a profile picture (JPG, PNG; max 5 MB)</p>
        </div>

        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.name')}</label>
            <input type="text" value={profileData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.namePlaceholder')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.email')}</label>
            <input type="email" value={profileData.email} readOnly className={`${fieldClass} vscode-text-tertiary bg-opacity-50 cursor-not-allowed`} placeholder={t('dashboard.userProfile.profileInfo.email')} title={t('dashboard.userProfile.profileInfo.cannotBeChanged')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.phone')}</label>
            <input type="tel" value={profileData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.phonePlaceholder')} />
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.userProfile.profileInfo.gender')}</label>
            <select value={profileData.gender} onChange={(e) => handleInputChange('gender', e.target.value)} className={fieldClass}>
              <option value="">{t('dashboard.userProfile.profileInfo.genderPreferNot')}</option>
              <option value="female">{t('dashboard.userProfile.profileInfo.female')}</option>
              <option value="male">{t('dashboard.userProfile.profileInfo.male')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t vscode-border">
        <p className="text-sm font-medium vscode-text-secondary mb-2">{t('dashboard.userProfile.profileInfo.address')} <span className="font-normal vscode-text-tertiary">({t('dashboard.userProfile.profileInfo.optional').toLowerCase()})</span></p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <input type="text" value={profileData.street_address} onChange={(e) => handleInputChange('street_address', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.streetAddress')} />
          </div>
          <div>
            <input type="text" value={profileData.city} onChange={(e) => handleInputChange('city', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.city')} />
          </div>
          <div>
            <input type="text" value={profileData.country} onChange={(e) => handleInputChange('country', e.target.value)} className={fieldClass} placeholder={t('dashboard.userProfile.profileInfo.country')} />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button type="button" onClick={handleSaveProfile} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3] touch-manipulation" style={{ background: 'var(--brand-cta-bg)' }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? t('dashboard.userProfile.profileInfo.saving') : t('dashboard.userProfile.profileInfo.saveProfile')}
        </button>
      </div>
    </section>
  );
});

export default React.memo(ProfileInformationTab);
